import type {ChildModel} from 'app/modules/auth/core/_models'
import Avatar from 'common/components/Avatar'
import {Select} from 'common/components/Fields'
import {getSelectedChildId, setSelectedChildId} from 'common/helpers/selectedChild'
import {FC} from 'react'

import {useT} from '_metronic/i18n/t'

type ChildOption = {value: number; label: string; child: ChildModel}

/**
 * Avatar + name + class, used for both the menu rows and the selected value.
 * `Avatar` falls back to initials when there is no picture or it fails to load.
 */
const ChildOptionLabel: FC<{child: ChildModel}> = ({child}) => (
  <span className='d-flex align-items-center' data-testid='child-option'>
    <Avatar
      src={child.profilePicture || undefined}
      alt={child.name}
      label={child.name}
      showLabel={!child.profilePicture}
      size='25'
      circle
      className='me-2 flex-shrink-0'
    />
    <span className='text-truncate'>
      {child.name}
      {child.className && <span className='text-muted fs-8 ms-1'>({child.className})</span>}
    </span>
  </span>
)

/**
 * Parent header control to switch the child every child-scoped page and
 * request refers to. Persists the choice and reloads, because the pages read
 * the selection at mount (see `common/helpers/selectedChild`).
 */
const ChildSwitcher: FC<{list?: ChildModel[] | null; onSwitch?: () => void}> = ({
  list: kids,
  onSwitch,
}) => {
  const t = useT()
  const list = kids ?? []
  const selectedChildId = getSelectedChildId(list)

  if (!list.length) return null

  const options: ChildOption[] = list.map((child) => ({
    value: child.id,
    label: `${child.name}${child.className ? ` (${child.className})` : ''}`,
    child,
  }))

  const handleChange = (id: string | number) => {
    if (String(id) === selectedChildId) return
    setSelectedChildId(id)
    if (onSwitch) onSwitch()
    else window.location.reload()
  }

  return (
    <div className='w-200px w-xxl-250px' data-testid='child-switcher'>
      <Select
        aria-label={t('PARENT.SELECT_CHILD')}
        value={selectedChildId}
        onChange={handleChange}
        options={options}
        isSearchable={list.length > 5}
        formatOptionLabel={(option: unknown) => (
          <ChildOptionLabel child={(option as ChildOption).child} />
        )}
      />
    </div>
  )
}

export default ChildSwitcher
